import { Action, AnyAction, Dispatch, Reducer } from 'redux';
import { ErrorReporterService } from '../services';
import { retryPromise } from '../utils';
import { ReducerManager, ServiceRegistry } from './managers';
import { AnyModuleLoader, Module, ModuleLoaderAnyDependencies } from './types';

const consoleErrorReporterService: ErrorReporterService = {
    // eslint-disable-next-line no-console
    addBreadcrumb: ({ message }) => console.log(message),
    // eslint-disable-next-line no-console
    logWarning: ({ error, extra }) => console.warn(error, extra),
    // eslint-disable-next-line no-console
    logError: ({ error, extra }) => console.error(error, extra),
};

export interface ModuleManager<S = unknown, A extends Action = AnyAction> {
    addModule: <M extends Module>(moduleLoader: ModuleLoaderAnyDependencies<M>) => Promise<M>;
    reduce: Reducer<S, A>;
    serviceRegistry: ServiceRegistry;
    setDispatch: (dispatch: unknown) => void;
}

/**
 * {@link https://app.getguru.com/card/ckKonEqi/Lazyloaded-modules-with-mewsmodules}
 */
export const createModuleManager = <S = unknown, A extends Action = AnyAction>({
    reducerManager,
    serviceRegistry,
    errorReporterService = consoleErrorReporterService,
}: {
    reducerManager: ReducerManager<S, A>;
    serviceRegistry: ServiceRegistry;
    errorReporterService?: ErrorReporterService;
}) => {
    const { addBreadcrumb, logError, logWarning } = errorReporterService;

    let dispatch: Dispatch | null = null;

    const promiseCache = new Map<AnyModuleLoader, Promise<Module>>();

    const loadChain: AnyModuleLoader[] = [];

    const addModule = async <M extends Module>(moduleLoader: ModuleLoaderAnyDependencies<M>) => {
        // cyclic dependency guard
        if (loadChain.includes(moduleLoader)) {
            const error = new Error(`Cyclic dependency detected: ${loadChain.join(' -> ')}`);
            logError({ error });

            throw error;
        }
        loadChain.push(moduleLoader);

        // existence check is done in the cached method

        addBreadcrumb({ message: `Loading module '${moduleLoader.toString()}':` });

        // load deps first
        let dependencies: AnyModuleLoader[] = [];
        if (moduleLoader.dependencies) {
            addBreadcrumb({ message: '-- found dependencies, loading them first' });
            dependencies = await Promise.all(moduleLoader.dependencies.map(cachedAddModule));
        }

        addBreadcrumb({ message: '-- fetching' });
        let module;
        try {
            const onBeforeTry = (attempts: number) =>
                addBreadcrumb({ message: `-- trying to fetch module: attempts left: ${attempts - 1}` });
            const { createModule } = await retryPromise(moduleLoader.load, { onBeforeTry });
            addBreadcrumb({ message: '-- fetched, creating module' });
            module = createModule(dependencies);
        } catch (error) {
            let webdriverDetected = false;
            if (window.document.documentElement.getAttribute('webdriver')) {
                webdriverDetected = true;
                addBreadcrumb({ message: '-- WebDriver detected by HTML attribute' });
            }
            if (window.navigator.webdriver) {
                webdriverDetected = true;
                addBreadcrumb({ message: '-- WebDriver detected by navigator.webdriver' });
            }
            logWarning({
                error: `Module fetching failed${webdriverDetected ? ' (WebDriver detected)' : ''}: ${error}`,
            });
            loadChain.pop();
            throw error;
        }
        addBreadcrumb({ message: `-- module fetched` });

        // inject reducer
        if (module.reducer) {
            reducerManager.add(module.id, module.reducer);

            // seed reducer
            if (dispatch) {
                dispatch({ type: '@@internal/moduleManager/reducerSeeded' });
            }
        }

        if (Array.isArray(module.services)) {
            addBreadcrumb({ message: '-- adding services' });
            module.services.forEach(serviceRegistry.add);
        }

        loadChain.pop();
        return module;
    };

    const cachedAddModule = async <M extends Module>(moduleLoader: ModuleLoaderAnyDependencies<M>) => {
        addBreadcrumb({ message: `Loading module '${moduleLoader.toString()}' through memoizer:` });
        if (promiseCache.has(moduleLoader)) {
            addBreadcrumb({ message: '-- module is already in cache' });
            return promiseCache.get(moduleLoader);
        }

        addBreadcrumb({ message: '-- module not found in cache, passing to loader' });
        const promise = addModule(moduleLoader);
        promiseCache.set(moduleLoader, promise);
        promise.catch((error) => {
            promiseCache.delete(moduleLoader);
            throw error;
        });

        addBreadcrumb({ message: '-- module added to cache' });
        return promise;
    };

    const setDispatch = (d: Dispatch) => (dispatch = d);

    return {
        addModule: cachedAddModule,
        reduce: reducerManager.reduce,
        serviceRegistry,
        setDispatch,
    };
};
